import type { HDEExistingSubmission } from '~/routes/remix-api.app.hde-existing-submission';
import type { LocationOption } from '../components/registration/types';

/**
 * Parse a boolean stored on the submission. The GraphQL EventSubmissionFormField
 * resolver stringifies booleans as "Yes"/"No" — accept the other common forms too.
 */
function parseYesNo(value: string | undefined): boolean | null {
    if (value == null) return null;
    const v = value.trim().toLowerCase();
    if (v === 'yes' || v === 'true' || v === '1') return true;
    if (v === 'no' || v === 'false' || v === '0') return false;
    return null;
}

/**
 * Map the shared guest-invitation fields (Homeowner + Professional). Returns the
 * partial form-data slice; Exhibitor forms ignore these keys.
 */
function mapGuestFields(
    fields: Record<string, string>,
): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    const bringGuest = parseYesNo(fields.BringingGuest);
    if (bringGuest === null) return out;

    out.bringGuest = bringGuest;
    if (bringGuest) {
        const guestName = [fields.GuestFirstName, fields.GuestLastName]
            .filter(Boolean)
            .join(' ')
            .trim();
        if (guestName) out.guestName = guestName;
        if (fields.GuestEmail) out.guestEmail = fields.GuestEmail;
        // Open the guest form view directly so the prefilled name/email render.
        out.guestFormView = true;
    }
    return out;
}

/**
 * Resolve the numeric EventID the user is registering for, used to look up an
 * existing submission. Prefers an explicit form/social-login selection, then
 * falls back to the first location option for single-event flows where eventId
 * is still empty on the form.
 */
export function resolveHdeEventId(
    eventId: string | null,
    locationOptions: LocationOption[],
    socialLoginEventId: string | null,
    allowLocationFallback = true,
): number | null {
    const source =
        eventId ??
        socialLoginEventId ??
        (allowLocationFallback && locationOptions.length > 0
            ? (locationOptions[0]?.value ?? null)
            : null);
    if (!source) {
        return null;
    }
    const first = source.split(',')[0]?.trim();
    const parsed = first ? Number(first) : Number.NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Parse a stored multi-select answer into the shape the HDE multi-select steps expect
 * (`{ value, label }[]`) so the matching checkboxes light up. The submission persists
 * these as a "; "-joined string (see PHP `ProjectFields::getProjectFields`), so that is
 * the primary format; a JSON array is also accepted in case a raw value is ever stored
 * that way. The submit payload only persists the `.value`, so we mirror it as the label
 * too — the step renders its catalogue's label, not the value-array's, so this is fine.
 */
function parseMultiSelect(
    value: string | undefined,
): Array<{ value: string; label: string }> | null {
    if (!value) return null;

    const toItems = (values: string[]) => {
        const items = values
            .map((v) => v.trim())
            .filter((v) => v.length > 0)
            .map((v) => ({ value: v, label: v }));
        return items.length > 0 ? items : null;
    };

    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
            return toItems(
                parsed.filter((v): v is string => typeof v === 'string'),
            );
        }
    } catch {
        // Not JSON — fall through to the "; "-joined string format.
    }

    return toItems(value.split(';'));
}

/**
 * Map a previously-submitted Homeowner record onto the homeowner form's data shape.
 *
 * Scalar fields, the boolean guest invitation, and JSON-stringified multi-select
 * answers (attendance reasons, product interests) are reconstructed so the matching
 * option buttons render as selected. Structured location objects are intentionally
 * omitted — they cannot be reconstructed from the stringified submission without the
 * full DetailedPlace catalogue, so the user re-enters those.
 */
export function mapExistingSubmissionToHomeownerFormData(
    submission: HDEExistingSubmission,
): Record<string, unknown> {
    const fields = submission.fields;
    const out: Record<string, unknown> = {};

    if (
        fields.ProjectType === 'Residential' ||
        fields.ProjectType === 'Commercial'
    ) {
        out.projectType = fields.ProjectType;
    }
    if (fields.ProjectScope) out.projectScope = fields.ProjectScope;
    if (fields.ProjectStage) out.projectStage = fields.ProjectStage;
    if (fields.ProjectLocation) out.projectLocation = fields.ProjectLocation;
    if (fields.ProjectBudget) out.budget = fields.ProjectBudget;
    if (fields.HDEOtherPleaseSpecify)
        out.otherReason = fields.HDEOtherPleaseSpecify;

    const attendanceReasons = parseMultiSelect(fields.ProjectAttendanceReason);
    if (attendanceReasons) out.attendanceReasons = attendanceReasons;
    const productInterests = parseMultiSelect(fields.ProjectProductInterest);
    if (productInterests) out.productInterests = productInterests;

    Object.assign(out, mapGuestFields(fields));

    return out;
}

/**
 * Map a previously-submitted Professional/Exhibitor record onto the company form's
 * data shape. Scalar fields only (see homeowner note above for the structured-field
 * caveat). Guest fields are included for the Professional flow; the Exhibitor form
 * does not consume them.
 */
export function mapExistingSubmissionToCompanyFormData(
    submission: HDEExistingSubmission,
): Record<string, unknown> {
    const fields = submission.fields;
    const out: Record<string, unknown> = {};

    const fullName = [submission.firstName, submission.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();
    if (fullName) out.fullName = fullName;
    if (submission.email) out.companyEmail = submission.email;

    const phone = submission.phone ?? fields.Phone ?? null;
    if (phone) out.phone = phone;
    if (fields.CompanyName) out.companyName = fields.CompanyName;
    if (fields.CompanyWebsite) out.companyWebsite = fields.CompanyWebsite;
    if (fields.CompanyType) out.companyType = fields.CompanyType;
    if (fields.CompanyLocation) out.companyLocation = fields.CompanyLocation;

    Object.assign(out, mapGuestFields(fields));

    return out;
}
